import type { CSSProperties, HTMLAttributes, ReactNode } from "react";
import { cn } from "../utils/cx.js";

export interface SpaceProps extends HTMLAttributes<HTMLDivElement> {
  direction?: "horizontal" | "vertical";
  size?: "sm" | "md" | "lg";
  wrap?: boolean;
  children: ReactNode;
}

const SPACE_GAP = { sm: "gap-2", md: "gap-3", lg: "gap-4" } as const;

export function Space({
  direction = "horizontal",
  size = "md",
  wrap,
  className,
  children,
  ...rest
}: SpaceProps) {
  return (
    <div
      className={cn(
        "inline-flex",
        direction === "vertical" ? "flex-col" : "flex-row items-center",
        wrap && "flex-wrap",
        SPACE_GAP[size],
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export function Divider({ vertical, className }: { vertical?: boolean; className?: string }) {
  if (vertical) {
    return (
      <span
        role="separator"
        aria-orientation="vertical"
        className={cn("mx-2 inline-block h-4 w-px bg-border align-middle", className)}
      />
    );
  }
  return <div role="separator" className={cn("my-3 h-px w-full bg-border", className)} />;
}

/* --- App-shell layout ------------------------------------------------------
   The visual chrome comes from the .app-* classes in apps/web/src/styles.css
   (unlayered, so they win over Tailwind utilities); these elements just forward
   className + provide sane fallbacks for non-shell usage. */

export interface LayoutProps {
  hasSider?: boolean;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}

export function Layout({ hasSider, className, style, children }: LayoutProps) {
  return (
    <section
      className={cn("flex min-h-0 flex-1", hasSider ? "flex-row" : "flex-col", className)}
      style={style}
    >
      {children}
    </section>
  );
}

export type SiderBreakpoint = "xs" | "sm" | "md" | "lg" | "xl" | "xxl";

export interface SiderProps {
  width?: number;
  collapsedWidth?: number;
  collapsible?: boolean;
  collapsed?: boolean;
  onCollapse?: (collapsed: boolean) => void;
  breakpoint?: SiderBreakpoint;
  /** Pass `null` to hide any default trigger (render your own). */
  trigger?: ReactNode | null;
  theme?: "light" | "dark";
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}

export function Sider({
  width = 240,
  collapsedWidth = 80,
  collapsed,
  className,
  style,
  children,
}: SiderProps) {
  const w = collapsed ? collapsedWidth : width;
  return (
    <aside
      className={cn("shrink-0 transition-[width] duration-200", className)}
      style={{ width: w, minWidth: w, maxWidth: w, ...style }}
    >
      {children}
    </aside>
  );
}

export interface LayoutSlotProps {
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}

export function Header({ className, style, children }: LayoutSlotProps) {
  return (
    <header className={cn("flex items-center", className)} style={style}>
      {children}
    </header>
  );
}

export function Content({ className, style, children }: LayoutSlotProps) {
  return (
    <main className={cn("min-h-0 flex-1", className)} style={style}>
      {children}
    </main>
  );
}
